import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { AuthResponse, SessionUser, LoginRequest, ChangePasswordRequest, SessionInfo } from '@/types'
import { api, ApiClientError } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  session: () => [...authKeys.all, 'session'] as const,
}

/**
 * Main auth hook that provides login, logout, password change, and auto session refresh
 *
 * Authentication is cookie-based (httpOnly session cookie, Better Auth, native Postgres session store):
 * - Session cookie: better-auth.session_token, TTL 24h
 * - Auto refresh: every 4 minutes to keep session alive
 */
export function useAuth() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  // Set by ProtectedRoute on a fresh visit (router state only, never a query param, so no open redirect).
  const from = (useLocation().state as { from?: { pathname: string; search?: string } } | null)?.from
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Auth store state (user and auth status - cookies handle actual auth)
  const {
    user,
    isAuthenticated,
    forcePasswordChange,
    setAuth,
    clearAuth,
    setForcePasswordChange,
  } = useAuthStore()

  // Cookie-based auth: cookies are set by the server. Store user info locally,
  // drop cached session data, then navigate. Shared by login and invite accept.
  const completeSignIn = (data: { user: SessionUser; forcePasswordChange?: boolean }) => {
    setAuth({
      user: data.user,
      forcePasswordChange: data.forcePasswordChange ?? false,
    })

    // Invalidate any cached session data
    queryClient.invalidateQueries({ queryKey: authKeys.session() })

    // Navigate based on force password change
    if (data.forcePasswordChange) {
      navigate('/change-password', { replace: true })
    } else {
      // Role-based landing (RoleLanding at "/" dispatches: advisor → /dashboard,
      // WRISE/admin → /cases). Avoids bouncing non-advisor roles to
      // /access-denied via the advisor-only /dashboard guard.
      navigate(from ? from.pathname + (from.search ?? '') : '/', { replace: true })
    }
  }

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const response = await api.post<AuthResponse>('/auth/login', credentials, {
        skipAuth: true,
      })
      if (!response.data) {
        throw new ApiClientError('Login failed', 500, 'LOGIN_FAILED')
      }
      return response.data
    },
    onSuccess: completeSignIn,
    onError: (error) => {
      if (import.meta.env.DEV) {
        console.error('[Auth] Login error:', error)
      }
    },
  })

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Always try to logout - cookies will be cleared by server
      try {
        await api.post('/auth/logout')
      } catch (error) {
        // Ignore errors - we still want to clear local state
        if (import.meta.env.DEV) {
          console.warn('[Auth] Logout API error (ignored):', error)
        }
      }
    },
    onSettled: () => {
      // Always clear auth state, even if API call fails
      clearAuth()
      queryClient.clear()
      navigate('/login', { replace: true })
    },
  })

  // Session refresh mutation (cookie-based auth)
  // Calling /auth/session validates session and extends cookie
  const refreshMutation = useMutation({
    mutationFn: async () => {
      // Just call session endpoint - cookies are sent automatically
      const response = await api.get<{ user: unknown }>('/auth/session')

      if (!response.data) {
        throw new ApiClientError('Session expired', 401, 'SESSION_EXPIRED')
      }

      return response.data
    },
    onSuccess: () => {
      if (import.meta.env.DEV) {
        console.info('[Auth] Session refreshed successfully')
      }
    },
    onError: (error) => {
      if (import.meta.env.DEV) {
        console.error('[Auth] Session refresh failed:', error)
      }
      // Clear auth on refresh failure - session has expired
      clearAuth()
      navigate('/login', { replace: true })
    },
  })

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: ChangePasswordRequest) => {
      const response = await api.post('/auth/password', data)
      return response
    },
    onSuccess: () => {
      // Clear force password change flag
      setForcePasswordChange(false)

      if (import.meta.env.DEV) {
        console.info('[Auth] Password changed successfully')
      }
    },
  })

  // Auto session refresh — periodically call session endpoint to keep session alive
  const refreshSessionIfNeeded = useCallback(async () => {
    if (!isAuthenticated) return

    // Always refresh - the backend will handle cookie cache refresh
    // This keeps the session alive by triggering activity
    if (import.meta.env.DEV) {
      console.info('[Auth] Refreshing session...')
    }
    refreshMutation.mutate()
  }, [isAuthenticated, refreshMutation])

  // Set up auto session refresh timer
  // Refresh every 4 minutes to keep 5-minute cookie cache fresh
  useEffect(() => {
    if (isAuthenticated) {
      // Set up interval (refresh every 4 minutes)
      const REFRESH_INTERVAL = 4 * 60 * 1000 // 4 minutes
      refreshTimerRef.current = setInterval(
        refreshSessionIfNeeded,
        REFRESH_INTERVAL
      )

      if (import.meta.env.DEV) {
        console.info('[Auth] Session refresh timer started (every 4 minutes)')
      }

      return () => {
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current)
          if (import.meta.env.DEV) {
            console.info('[Auth] Session refresh timer stopped')
          }
        }
      }
    }
  }, [isAuthenticated, refreshSessionIfNeeded])

  // Actions
  const login = useCallback(
    (credentials: LoginRequest) => loginMutation.mutateAsync(credentials),
    [loginMutation]
  )

  const logout = useCallback(() => logoutMutation.mutate(), [logoutMutation])

  // Reset login error state (call when navigating to login page)
  const resetLoginError = useCallback(() => {
    loginMutation.reset()
  }, [loginMutation])

  const changePassword = useCallback(
    (data: ChangePasswordRequest) => changePasswordMutation.mutateAsync(data),
    [changePasswordMutation]
  )

  const refreshSession = useCallback(
    () => refreshMutation.mutateAsync(),
    [refreshMutation]
  )

  return {
    // State
    user,
    isAuthenticated,
    forcePasswordChange,

    // Actions
    login,
    logout,
    changePassword,
    refreshSession,
    resetLoginError,
    completeSignIn,

    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,
    isRefreshing: refreshMutation.isPending,

    // Errors
    loginError: loginMutation.error,
    changePasswordError: changePasswordMutation.error,
  }
}

/**
 * Hook to fetch current session info
 */
export function useSession() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  return useQuery({
    queryKey: authKeys.session(),
    queryFn: async () => {
      const response = await api.get<SessionInfo>('/auth/session')
      return response.data
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  })
}
